import { useQuery, useReactiveVar } from '@apollo/client'
import { Box, styled } from '@mui/material'
import { useUnleashContext } from '@unleash/proxy-client-react'
import { getAuth } from 'firebase/auth'
import { PropsWithChildren, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import Loading from '../../../components/PageLoading'
import { BucketUserStatus, UserProfileDocument } from '../../../graphql/generated'
import { adminUserIdVar, modalVar } from '../../../providers/apollo/cache'
import logger from '../../../utils/logger'
import DisconnectWhatsAppAccount from '../../integrations/whatsapp/components/modals/DisconnectWhatsAppAccount'
import DisconnectWhatsAppGroup from '../../integrations/whatsapp/components/modals/DisconnectWhatsAppGroup'
import useBucket from '../hooks/useBucket'
import useBucketGallerySettings from '../hooks/useBucketGallerySettings'
import Alerts from './Alerts'
import ClearSelected from './ClearSelected'
import AppBar from './appbar'
import Banned from './blockers/BucketDisabled'
import EmailNotVerified from './blockers/EmailNotVerified'
import NotFound from './blockers/NotFound'
import Drawer from './drawer'
import AlbumDetailsDrawer from './drawers/AlbumDetails'
import AlbumMoveDrawer from './drawers/AlbumMove'
import MediaDetailsDrawer from './drawers/MediaDetails'
import MediaMoveDrawer from './drawers/MediaMove'
import MediaRestoreDrawer from './drawers/MediaRestore'
import AlbumDeleteModal from './modals/AlbumDelete'
import BucketCreateModal from './modals/BucketCreate'
import BucketDelete from './modals/BucketDelete'
import BucketRename from './modals/BucketRename'
import BucketSettings from './modals/BucketSettings'
import BucketTrashEmpty from './modals/BucketTrashEmpty'
import BucketUpgrade from './modals/BucketUpgrade'
import FilterAndSort from './modals/FilterAndSort'
import InvitationCreateModal from './modals/InvitationCreate'
import MediaDelete from './modals/MediaDelete'
import MediaDeletePermanently from './modals/MediaDeletePermanently'
import SharingLinkCreate from './modals/MediaShareCreate'
import MediaShareDelete from './modals/MediaShareDelete'
import MediaShareUpdate from './modals/MediaShareUpdate'
import PremiumFeatureModal from './modals/PremiumFeatureModal'
import SimpleUpsellToast from './modals/SimpleUpsellToast'
import SubalbumCreate from './modals/SubalbumCreate'
import TermsAndConditions from './modals/TermsAndConditions'
import UpsellModal from './modals/UpsellModal'
import Pill from './shared/Pill'
import { Blocker } from './types'

const Main = styled('main')({
  display: 'flex',
  flex: 1,
  overflowX: 'hidden',
  flexDirection: 'column',
  position: 'relative'
})

const Wrapper = styled('div')(() => ({
  display: 'flex',
  flex: 1
}))

interface BlockerProps {
  blocker: Blocker
}

export function RenderBlocker({ blocker }: Readonly<BlockerProps>) {
  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <AppBar iconMenu={false} searchBar={false} showSelectButton={false} />
        <Wrapper>
          <Drawer />
          <Main id="main">
            {blocker === Blocker.EMAIL_NOT_VERIFIED && <EmailNotVerified />}
            {blocker === Blocker.BUCKET_DISABLED && <Banned />}
            {blocker === Blocker.NOT_FOUND && <NotFound />}
          </Main>
        </Wrapper>
      </div>
      <Alerts />
    </>
  )
}

export function FullscreenLayout({ children }: Readonly<PropsWithChildren<{}>>) {
  const { bucket, bucketUser, loading } = useBucket()

  if (loading) {
    return <Loading />
  }

  if (!getAuth().currentUser?.emailVerified) {
    return <RenderBlocker blocker={Blocker.EMAIL_NOT_VERIFIED} />
  }

  if (bucket?.isBanned) {
    return <RenderBlocker blocker={Blocker.BUCKET_DISABLED} />
  }

  if (bucketUser?.userStatus !== BucketUserStatus.Active) {
    return <RenderBlocker blocker={Blocker.NOT_FOUND} />
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Wrapper>
        <Main id="main">{children}</Main>
      </Wrapper>
      <AlbumDeleteModal />
      <AlbumDetailsDrawer />
      <Alerts />
      <BucketUpgrade />
      <BucketDelete />
      <ClearSelected />
      <InvitationCreateModal />
      <MediaDelete />
      <MediaDetailsDrawer />
      <MediaMoveDrawer />
      <BucketSettings />
      <MediaRestoreDrawer />
      <SharingLinkCreate />
      <UpsellModal />
      <PremiumFeatureModal />
    </div>
  )
}

export function NewAccountLayout({ children }: Readonly<PropsWithChildren<{}>>) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <AppBar searchBar={false} displayDrawerToggle={false} iconMenu={false} showSelectButton={false} />
      <Wrapper>
        <Main id="main">{children}</Main>
      </Wrapper>
      <Alerts />
    </div>
  )
}

export function SettingsLayout({ children }: Readonly<PropsWithChildren<{}>>) {
  const location = useLocation()
  const isSearchBarEnabled = !location.pathname.includes('settings') && !location.pathname.includes('buckets')

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <AppBar displayDrawerToggle={false} iconMenu={false} showSelectButton={false} searchBar={isSearchBarEnabled} />
      <Wrapper>
        <Main id="main">{children}</Main>
      </Wrapper>
      <Alerts />
      <BucketCreateModal />
      <BucketDelete />
      <BucketUpgrade />
      <BucketSettings />
      <ClearSelected />
      <PremiumFeatureModal />
      <UpsellModal />
      <InvitationCreateModal />
    </div>
  )
}

export function HomePageLayout({ children }: Readonly<PropsWithChildren<{}>>) {
  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <AppBar showSelectButton={false} inviteAndSharingAvatars searchBar={false} />
        <Wrapper>
          <Drawer />
          <Main id="main">{children}</Main>
          <Pill />
        </Wrapper>
      </div>
      <BucketCreateModal />
      <PremiumFeatureModal />
      <UpsellModal />
      <BucketUpgrade />
      <InvitationCreateModal />
    </>
  )
}

export function WhatsAppLayout({ children }: Readonly<PropsWithChildren<{}>>) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <AppBar displayDrawerToggle={false} iconMenu={false} showSelectButton={false} searchBar={false} />
      <Wrapper>
        <Main id="main">{children}</Main>
      </Wrapper>
      <BucketSettings />
      <Alerts />
    </div>
  )
}

export function InviteLayout({ children }: Readonly<PropsWithChildren<{}>>) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <AppBar displayDrawerToggle={false} iconMenu={false} searchBar={false} showSelectButton={false} settingsMenu={false} inviteAndSharingAvatars />
      <Wrapper sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Main id="main">{children}</Main>
      </Wrapper>
      <BucketUpgrade />
      <BucketSettings />
      <Alerts />
    </div>
  )
}

export function TransferLayout({ children }: Readonly<PropsWithChildren<{}>>) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <AppBar iconMenu={false} showSelectButton={false} />
      <Wrapper sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Main id="main">{children}</Main>
      </Wrapper>
      <Alerts />
    </div>
  )
}

export function ActivityLayout({ children }: Readonly<PropsWithChildren<{}>>) {
  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'hidden' }}>
        <AppBar showSelectButton={false} inviteAndSharingAvatars />
        <Wrapper>
          <Drawer />
          <Main id="main">{children}</Main>
          <Pill />
        </Wrapper>
      </div>
      <BucketCreateModal />
      <PremiumFeatureModal />
      <UpsellModal />
      <BucketUpgrade />
      <InvitationCreateModal />
    </>
  )
}

export function DownloadsLayout({ children }: Readonly<PropsWithChildren<{}>>) {
  const { bucket, bucketUser, loading } = useBucket()

  if (loading) {
    return <Loading />
  }

  if (!getAuth().currentUser?.emailVerified) {
    return <RenderBlocker blocker={Blocker.EMAIL_NOT_VERIFIED} />
  }

  if (bucket?.isBanned) {
    return <RenderBlocker blocker={Blocker.BUCKET_DISABLED} />
  }

  if (bucketUser?.userStatus !== BucketUserStatus.Active) {
    return <RenderBlocker blocker={Blocker.NOT_FOUND} />
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <AppBar iconMenu={false} searchBar={false} showSelectButton={false} inviteAndSharingAvatars />
        <Wrapper>
          <Drawer />
          <Main id="main">{children}</Main>
        </Wrapper>
      </div>
      <Alerts />
      <BucketCreateModal />
      <PremiumFeatureModal />
      <UpsellModal />
      <BucketSettings />
      <BucketUpgrade />
    </>
  )
}

// we should start adding more layout properties to move the logic from inside
// the appbar and other specific components up to the including page component itself
// (see Trash)
interface GalleryLayoutProps {
  showSelectButton?: boolean
}

export function GalleryLayout({ children, showSelectButton = true }: Readonly<PropsWithChildren<GalleryLayoutProps>>) {
  const profileQuery = useQuery(UserProfileDocument)
  const profileLoaded = Boolean(profileQuery.data?.userProfile)
  const { bucket, bucketUser, loading } = useBucket()
  const { updateSettings, normalize } = useBucketGallerySettings()
  const unleashContext = useUnleashContext()
  const isAdmin = useReactiveVar(adminUserIdVar)
  const { data } = useQuery(UserProfileDocument, {
    onCompleted: (profileData) => {
      unleashContext({ userId: profileData?.userProfile?.id, properties: { planId: profileData.userProfile?.plan?.plan_name as string } })
    }
  })
  const updateSettingsRef = useRef(updateSettings)
  const normalizeRef = useRef(normalize)
  const desktopSettings = data?.userProfile?.desktopSettings

  useEffect(() => {
    if (!desktopSettings) return

    const newSettings = normalizeRef.current(desktopSettings)
    updateSettingsRef.current(newSettings, { skipMutation: true })
  }, [desktopSettings])

  useEffect(() => {
    if (!isAdmin) {
      const latestTermsAccepted = Boolean(profileQuery.data?.userProfile?.latestTermsAccepted)
      if (profileLoaded && !latestTermsAccepted) {
        logger.info('Terms and conditions not accepted')
        modalVar('termsAndConditions')
      }
    }
  }, [isAdmin, profileLoaded, profileQuery.data?.userProfile?.latestTermsAccepted])

  if (loading) {
    return <Loading />
  }

  if (!getAuth().currentUser?.emailVerified) {
    return <RenderBlocker blocker={Blocker.EMAIL_NOT_VERIFIED} />
  }

  if (bucket?.isBanned) {
    return (
      <>
        <RenderBlocker blocker={Blocker.BUCKET_DISABLED} />
        <BucketCreateModal />
      </>
    )
  }

  if (bucketUser?.userStatus !== BucketUserStatus.Active) {
    return <RenderBlocker blocker={Blocker.NOT_FOUND} />
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflowY: 'hidden' }}>
        <Box className="pendo-banner" />
        <AppBar showSelectButton={showSelectButton} inviteAndSharingAvatars />
        <Wrapper>
          <Drawer />
          <Main id="main" sx={{ overflowY: 'hidden' }}>
            {children}
          </Main>
          <Pill />
        </Wrapper>
      </div>
      <AlbumDeleteModal />
      <AlbumDetailsDrawer />
      <AlbumMoveDrawer />
      <Alerts />
      <BucketCreateModal />
      <BucketDelete />
      <BucketRename />
      <PremiumFeatureModal />
      <UpsellModal />
      <SimpleUpsellToast />
      <BucketTrashEmpty />
      <BucketUpgrade />
      <ClearSelected />
      <FilterAndSort />
      <BucketSettings />
      <InvitationCreateModal />
      <MediaDelete />
      <MediaDeletePermanently />
      <MediaDetailsDrawer />
      <MediaMoveDrawer />
      <MediaRestoreDrawer />
      <SharingLinkCreate />
      <SubalbumCreate />
      <MediaShareDelete />
      <MediaShareUpdate />
      <TermsAndConditions />
      <DisconnectWhatsAppAccount />
      <DisconnectWhatsAppGroup />
    </>
  )
}

export function EditorLayout({ children }: Readonly<PropsWithChildren<{}>>) {
  const { bucket, bucketUser, loading } = useBucket()

  if (loading) {
    return <Loading />
  }

  if (!getAuth().currentUser?.emailVerified) {
    return <RenderBlocker blocker={Blocker.EMAIL_NOT_VERIFIED} />
  }

  if (bucket?.isBanned) {
    return <RenderBlocker blocker={Blocker.BUCKET_DISABLED} />
  }

  if (bucketUser?.userStatus !== BucketUserStatus.Active) {
    return <RenderBlocker blocker={Blocker.NOT_FOUND} />
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <AppBar />
        <Wrapper>
          <Main id="main">{children}</Main>
        </Wrapper>
      </div>
      <AlbumDetailsDrawer />
      <Alerts />
      <ClearSelected />
      <MediaDelete />
      <MediaDetailsDrawer />
      <MediaMoveDrawer />
    </>
  )
}
